function toAmount(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

const WORKSHEET_SECTION_LABELS = {
  tr: {
    CURRENT_ASSETS: 'Donen Varliklar',
    NON_CURRENT_ASSETS: 'Duran Varliklar',
    CURRENT_LIABILITIES: 'Kisa Vadeli Yukumlulukler',
    NON_CURRENT_LIABILITIES: 'Uzun Vadeli Yukumlulukler',
    EQUITY: 'Ozkaynaklar',
    REVENUE: 'Hasilat',
    REVENUE_DEDUCTION: 'Hasilat Indirimleri',
    COST_OF_SALES: 'Satislarin Maliyeti',
    OPERATING_EXPENSE: 'Faaliyet Giderleri',
    OTHER_OPERATING_EXPENSE: 'Diger Faaliyet Giderleri',
    OTHER_OPERATING_INCOME: 'Diger Faaliyet Gelirleri',
    FINANCE_EXPENSE: 'Finansman Giderleri',
    FINANCE_INCOME: 'Finansman Gelirleri',
    NON_OPERATING_INCOME: 'Esas Faaliyet Disi Gelirler',
    NON_OPERATING_EXPENSE: 'Esas Faaliyet Disi Giderler',
    TAX_EXPENSE: 'Vergi Gideri',
    OCI: 'Diger Kapsamli Gelir'
  },
  en: {
    CURRENT_ASSETS: 'Current Assets',
    NON_CURRENT_ASSETS: 'Non-Current Assets',
    CURRENT_LIABILITIES: 'Current Liabilities',
    NON_CURRENT_LIABILITIES: 'Non-Current Liabilities',
    EQUITY: 'Equity',
    REVENUE: 'Revenue',
    REVENUE_DEDUCTION: 'Revenue Deductions',
    COST_OF_SALES: 'Cost of Sales',
    OPERATING_EXPENSE: 'Operating Expenses',
    OTHER_OPERATING_EXPENSE: 'Other Operating Expenses',
    OTHER_OPERATING_INCOME: 'Other Operating Income',
    FINANCE_EXPENSE: 'Finance Expenses',
    FINANCE_INCOME: 'Finance Income',
    NON_OPERATING_INCOME: 'Non-Operating Income',
    NON_OPERATING_EXPENSE: 'Non-Operating Expenses',
    TAX_EXPENSE: 'Tax Expense',
    OCI: 'Other Comprehensive Income'
  }
};

const WORKSHEET_LINE_LABELS = {
  tr: {
    INVENTORIES: 'Stoklar',
    PPE: 'Maddi Duran Varliklar',
    DEFERRED_TAX_ASSET: 'Ertelenmis Vergi Varligi',
    PERIOD_PROFIT_LOSS: 'Donem Net Kar Zarari',
    INVENTORY_WRITE_DOWN_EXPENSE: 'Stok Deger Dusuklugu Gideri',
    DEPRECIATION_ADJUSTMENT_INCOME: 'Amortisman Duzeltme Geliri',
    DEFERRED_TAX_EXPENSE: 'Ertelenmis Vergi Gideri Geliri',
    DEFERRED_TAX_EXPENSE_VUK: 'Ertelenmis Vergi Gideri Geliri',
    ROU_ASSET: 'Kullanim Hakki Varliklari',
    LEASE_LIABILITY_CURRENT: 'Kira Borcu (KV)',
    LEASE_LIABILITY_NON_CURRENT: 'Kira Borcu (UV)',
    DEPRECIATION_ROU: 'Kullanim Hakki Varligi Amortismani',
    INTEREST_LEASE: 'Kira Sozlesmesi Faiz Gideri',
    DEFERRED_TAX_LIABILITY: 'Ertelenmis Vergi Borcu',
    BALANCING_RESERVE: 'Dengeleme Rezervi'
  },
  en: {
    INVENTORIES: 'Inventories',
    PPE: 'Property, Plant and Equipment',
    DEFERRED_TAX_ASSET: 'Deferred Tax Asset',
    PERIOD_PROFIT_LOSS: 'Period Net Profit / Loss',
    INVENTORY_WRITE_DOWN_EXPENSE: 'Inventory Write-Down Expense',
    DEPRECIATION_ADJUSTMENT_INCOME: 'Depreciation Adjustment Income',
    DEFERRED_TAX_EXPENSE: 'Deferred Tax Expense / Income',
    DEFERRED_TAX_EXPENSE_VUK: 'Deferred Tax Expense / Income',
    ROU_ASSET: 'Right-of-Use Assets',
    LEASE_LIABILITY_CURRENT: 'Lease Liability (Current)',
    LEASE_LIABILITY_NON_CURRENT: 'Lease Liability (Non-Current)',
    DEPRECIATION_ROU: 'Right-of-Use Asset Depreciation',
    INTEREST_LEASE: 'Lease Contract Interest Expense',
    DEFERRED_TAX_LIABILITY: 'Deferred Tax Liability',
    BALANCING_RESERVE: 'Balancing Reserve'
  }
};

function translateWorksheetSection(value, isTr) {
  const labels = isTr ? WORKSHEET_SECTION_LABELS.tr : WORKSHEET_SECTION_LABELS.en;
  return labels[value] || value || '';
}

function translateWorksheetLineCode(value, isTr) {
  const labels = isTr ? WORKSHEET_LINE_LABELS.tr : WORKSHEET_LINE_LABELS.en;
  return labels[value] || value || '';
}

function formatBreakdownItem(item, isTr) {
  const title = isTr
    ? String(item?.standardTitleTr || item?.standardCode || '').trim()
    : String(item?.standardTitleEn || item?.standardCode || '').trim();
  const amount = toAmount(item?.amount);
  const explanation = isTr
    ? String(item?.explanationTr || '').trim()
    : String(item?.explanationEn || item?.explanationTr || '').trim();
  const amountLabel = Number.isFinite(amount) ? `${amount}` : '';

  return [title, amountLabel, explanation].filter(Boolean).join(' - ');
}

function buildRowFromAuditFormat(row) {
  return {
    sourceType: row.sourceType || '',
    sourceCode: row.sourceCode || '',
    sourceName: row.sourceName || '',
    vukAmount: toAmount(row.vukAmount),
    ifrsAccount: row.ifrsAccount || '',
    ifrsAccountName: row.ifrsAccountName || '',
    mappingAmount: toAmount(row.mappingAmount),
    recognitionAdjustment: toAmount(row.recognitionAdjustment),
    measurementAdjustment: toAmount(row.measurementAdjustment),
    ifrsEffect: toAmount(row.ifrsEffect),
    standardRef: row.standardRef || '',
    explanation: row.explanation || ''
  };
}

function buildRowFromWorksheetFormat(row, isTr) {
  const standardRefs = Array.isArray(row?.standardRefs) ? row.standardRefs.filter(Boolean) : [];
  const breakdown = Array.isArray(row?.adjustmentBreakdown) ? row.adjustmentBreakdown : [];
  const sourceName = isTr
    ? String(row.lineNameTr || translateWorksheetLineCode(row.lineCode, true) || '').trim()
    : String(row.lineNameEn || translateWorksheetLineCode(row.lineCode, false) || '').trim();

  return {
    sourceType: translateWorksheetSection(row.section, isTr),
    sourceCode: row.lineCode || '',
    sourceName,
    vukAmount: toAmount(row.vukAmount),
    ifrsAmount: toAmount(row.ifrsAmount),
    ifrsEffect: toAmount(row.delta),
    standardRef: standardRefs.join(', '),
    explanation: breakdown.map((item) => formatBreakdownItem(item, isTr)).filter(Boolean).join(' | ')
  };
}

function normalizeAuditRows(reportResponse) {
  const rows = Array.isArray(reportResponse?.lines) ? reportResponse.lines : [];
  return rows.map((row) => buildRowFromAuditFormat(row));
}

function normalizeWorksheetRows(reportResponse, isTr) {
  const rows = Array.isArray(reportResponse?.lines) ? reportResponse.lines : [];
  return rows
    .filter((row) => toAmount(row?.delta) !== 0)
    .map((row) => buildRowFromWorksheetFormat(row, isTr));
}

const LABELS = {
  tr: {
    reportTitle: 'VUK-IFRS Karsilastirma Raporu',
    statementName: 'VUK-IFRS Karsilastirma Raporu',
    noComparative: 'Bu rapor ayni donem icindeki VUK ve IFRS donusumunu karsilastirir; onceki donem karsilastirmasi kullanilmaz.',
    basisNoteAudit: 'Sunum, denetim odakli bir kopru olarak VUK kaynagi, IFRS hedef hesabi, mapping, recognition ve measurement etkilerini ilgili standart atiflariyla birlikte gosterir.',
    basisNoteWorksheet: 'Sunum, worksheet uzerinde uretilen IFRS satirlarini VUK tutari, IFRS tutari, net etki ve ilgili standart katkilarıyla birlikte gosterir.',
    sourceType: 'Kaynak Turu',
    sourceCode: 'Kaynak Kod',
    sourceName: 'Kaynak Hesap / Kural',
    vukAmount: 'VUK Tutar',
    ifrsAccount: 'IFRS Hesap',
    ifrsAccountName: 'IFRS Hesap Adi',
    mappingAmount: 'Mapping',
    recognitionAdjustment: 'Recognition',
    measurementAdjustment: 'Measurement',
    ifrsAmount: 'IFRS Tutar',
    ifrsEffect: 'IFRS Etkisi',
    standardRef: 'Standart Atfi',
    explanation: 'Aciklama',
    totalVukAmount: 'TOPLAM VUK',
    totalMappingAmount: 'TOPLAM MAPPING',
    totalRecognitionAdjustment: 'TOPLAM RECOGNITION',
    totalMeasurementAdjustment: 'TOPLAM MEASUREMENT',
    totalIfrsAmount: 'TOPLAM IFRS',
    totalIfrsEffect: 'TOPLAM IFRS ETKISI'
  },
  en: {
    reportTitle: 'Local GAAP to IFRS Comparison Report',
    statementName: 'Local GAAP to IFRS Comparison Report',
    noComparative: 'This report compares local GAAP and IFRS transformation within the same period; no previous-period comparison is used.',
    basisNoteAudit: 'Presentation acts as an audit-focused bridge showing the local GAAP source, target IFRS account, mapping, recognition and measurement effects together with the relevant standard references.',
    basisNoteWorksheet: 'Presentation shows worksheet-generated IFRS lines together with the local GAAP amount, IFRS amount, net effect and related standard contributions.',
    sourceType: 'Source Type',
    sourceCode: 'Source Code',
    sourceName: 'Source Account / Rule',
    vukAmount: 'Local GAAP Amount',
    ifrsAccount: 'IFRS Account',
    ifrsAccountName: 'IFRS Account Name',
    mappingAmount: 'Mapping',
    recognitionAdjustment: 'Recognition',
    measurementAdjustment: 'Measurement',
    ifrsAmount: 'IFRS Amount',
    ifrsEffect: 'IFRS Effect',
    standardRef: 'Standard Reference',
    explanation: 'Explanation',
    totalVukAmount: 'TOTAL LOCAL GAAP',
    totalMappingAmount: 'TOTAL MAPPING',
    totalRecognitionAdjustment: 'TOTAL RECOGNITION',
    totalMeasurementAdjustment: 'TOTAL MEASUREMENT',
    totalIfrsAmount: 'TOTAL IFRS',
    totalIfrsEffect: 'TOTAL IFRS EFFECT'
  }
};

export function buildVukIfrsComparisonPresentation({ reportResponse, isTr }) {
  const labels = isTr ? LABELS.tr : LABELS.en;
  const locale = isTr ? 'tr-TR' : 'en-US';
  const isWorksheetDetailed = reportResponse?.comparisonFormat === 'worksheetDetailed'
    || Object.prototype.hasOwnProperty.call(reportResponse?.lines?.[0] || {}, 'lineCode');

  if (isWorksheetDetailed) {
    const rows = normalizeWorksheetRows(reportResponse, isTr);
    const totals = rows.reduce((accumulator, row) => ({
      totalVukAmount: accumulator.totalVukAmount + toAmount(row.vukAmount),
      totalIfrsAmount: accumulator.totalIfrsAmount + toAmount(row.ifrsAmount),
      totalIfrsEffect: accumulator.totalIfrsEffect + toAmount(row.ifrsEffect)
    }), {
      totalVukAmount: 0,
      totalIfrsAmount: 0,
      totalIfrsEffect: 0
    });

    return {
      viewType: 'comparison',
      comparisonVariant: 'worksheet',
      labels,
      locale,
      comparativeAvailable: true,
      metadataNote: labels.basisNoteWorksheet,
      rows,
      totals
    };
  }

  const rows = normalizeAuditRows(reportResponse);
  const totals = rows.reduce((accumulator, row) => ({
    totalVukAmount: accumulator.totalVukAmount + toAmount(row.vukAmount),
    totalMappingAmount: accumulator.totalMappingAmount + toAmount(row.mappingAmount),
    totalRecognitionAdjustment: accumulator.totalRecognitionAdjustment + toAmount(row.recognitionAdjustment),
    totalMeasurementAdjustment: accumulator.totalMeasurementAdjustment + toAmount(row.measurementAdjustment),
    totalIfrsEffect: accumulator.totalIfrsEffect + toAmount(row.ifrsEffect)
  }), {
    totalVukAmount: 0,
    totalMappingAmount: 0,
    totalRecognitionAdjustment: 0,
    totalMeasurementAdjustment: 0,
    totalIfrsEffect: 0
  });

  return {
    viewType: 'comparison',
    comparisonVariant: 'audit',
    labels,
    locale,
    comparativeAvailable: true,
    metadataNote: labels.basisNoteAudit,
    isLegacyAudit: true,
    rows,
    totals: {
      totalVukAmount: reportResponse?.totalVukAmount == null
        ? totals.totalVukAmount
        : toAmount(reportResponse.totalVukAmount),
      totalMappingAmount: reportResponse?.totalMappingAmount == null
        ? totals.totalMappingAmount
        : toAmount(reportResponse.totalMappingAmount),
      totalRecognitionAdjustment: reportResponse?.totalRecognitionAdjustment == null
        ? totals.totalRecognitionAdjustment
        : toAmount(reportResponse.totalRecognitionAdjustment),
      totalMeasurementAdjustment: reportResponse?.totalMeasurementAdjustment == null
        ? totals.totalMeasurementAdjustment
        : toAmount(reportResponse.totalMeasurementAdjustment),
      totalIfrsEffect: reportResponse?.totalIfrsEffect == null
        ? totals.totalIfrsEffect
        : toAmount(reportResponse.totalIfrsEffect)
    }
  };
}
